import React, {useEffect} from "react";
import { createRoot } from 'react-dom/client';
import * as Sentry from "@sentry/react";
import {
    createBrowserRouter,
    RouterProvider
} from "react-router-dom";
import "./main.css"
import { LanguageProvider } from "/src/LanguageContext";
import { ToursProvider } from "/src/ToursContext";
import { SettingsProvider } from "/src/SettingsContext";
import { VisitedProvider } from "/src/VisitedContext";
import { PointProvider } from "/src/PointContext";
import { ModalProvider } from '/src/ModalContext';
import { MenuProvider } from '/src/MenuContext';
import { OverlayProvider } from "@/OverlayContext";
import { PointPreviewProvider } from '/src/PointPreviewContext';
import { NavigationProvider } from './src/NavigationContext';
import { LocationProvider } from './src/LocationContext';
import ErrorBoundary from '@/components/ErrorBoundary';

import './i18n';
import Info from "./src/pages/Info";
import axios from "axios";
import Home from "./src/pages/Home";
import SettingsService from "./src/services/SettingsService";
import Layout from "./src/Layout";
import Points from "./src/pages/Points";
import Point from "./src/pages/Point";
import NotFound from './src/pages/NotFound';
import Qrcode from "@/components/Qrcode";
import Tours from "@/pages/Tours";

// Initialize Sentry
Sentry.init({
    dsn: process.env.SENTRY_DSN,
    environment: process.env.NODE_ENV || 'development',
    release: process.env.NODE_ENV === 'production' 
        ? `production-${process.env.GITHUB_SHA || 'unknown'}`
        : process.env.NODE_ENV === 'staging'
            ? `staging-${process.env.GITHUB_SHA || 'unknown'}`
            : 'development',
    integrations: function (integrations) {
        // integrations will be all default integrations
        return integrations.filter(function (integration) {
            return integration.name !== "BrowserSession";
        });
    },
    beforeSend(event) {
        if (
            // ignore network and cors errors
            event.exception?.values?.[0]?.value?.includes('isTrusted') ||
            event.message?.includes('NetworkError')
        ) {
            return null; // Ignore event
        }
        return event;
    },
    // defaultIntegrations: false,
    // integrations: [
    //     Sentry.browserApiErrorsIntegration(),
    //     Sentry.dedupeIntegration(),
    //     Sentry.globalHandlersIntegration(),
    //     Sentry.inboundFiltersIntegration(),
    //     Sentry.linkedErrorsIntegration(),
    //     // Sentry.captureConsoleIntegration(),
    //     // Sentry.contextLinesIntegration(),
    //     // Sentry.httpClientIntegration(),
    //     // Sentry.reportingObserverIntegration(),
    //     // Sentry.rewriteFramesIntegration(),
    //     // Sentry.supabaseIntegration(),
    // ]
});

// set X-Tour-Host for all axios requests
axios.defaults.headers.common['X-Tour-Host'] = location.host;

const loadLanguage = async ({ params }) => {
    console.log("Language from URL params Index:", params, params.language);

    await SettingsService.detectLanguage(params);
    return true;
}

const router =
    createBrowserRouter([
        { path: "/:language?/qr/:qrcode", name: "qrcode", element: <Qrcode/>, loader: loadLanguage  },
        {
            element: <Layout/>,
            loader: loadLanguage,
            children: [
                { path: "/:language?/", name: "home", element: <Home/>, loader: loadLanguage  },
                { path: "/:language?/info", element: <Info/>, loader: loadLanguage },
                { path: "/:language?/list", element: <Points selectedView={'list'}/>, loader: loadLanguage },
                { path: "/:language?/points", element: <Points/>, loader: loadLanguage },
                { path: "/:language?/point/:point", element: <Points/>,
                    children: [
                        {
                            path: "page/:page",
                            element: <Point/>,
                            loader: loadLanguage
                        }
                    ],
                    loader: loadLanguage 
                },

            ]
        },
        { path: "/:language?/not-found", name: "not-found", element: <NotFound/>, loader: loadLanguage  },
]);


function AppWithCallbackAfterRender() {


    useEffect(() => {
        console.log('rendered');
    });

    return (
        <ErrorBoundary>
            <MenuProvider>
                <LanguageProvider>
                    <ToursProvider>
                        <SettingsProvider>
                            <VisitedProvider>
                                <LocationProvider>
                                    <NavigationProvider>
                                        <PointProvider>
                                            <ModalProvider>
                                                <OverlayProvider>
                                                    <PointPreviewProvider>
                                                        <RouterProvider router={router}/>
                                                    </PointPreviewProvider>
                                                </OverlayProvider>
                                            </ModalProvider>
                                        </PointProvider>
                                    </NavigationProvider>
                                </LocationProvider>
                            </VisitedProvider>
                        </SettingsProvider>
                    </ToursProvider>
                </LanguageProvider>
            </MenuProvider>
        </ErrorBoundary>
    )
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<AppWithCallbackAfterRender />);


// First we get the viewport height, and we multiply it by 1% to get a value for a vh unit
let vh = window.innerHeight * 0.01;
// Then we set the value in the --vh custom property to the root of the document
document.documentElement.style.setProperty('--vh', `${vh}px`);

// We listen to the resize event
window.addEventListener('resize', () => {
    // We execute the same script as before
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
});
